import React, { useState, useEffect } from 'react';

const Apply = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        instagram: '',
        portfolioUrl: '',
        note: ''
    });
    const [status, setStatus] = useState('idle'); // idle, submitting, success, error

    // Inject Google Fonts for the vintage look
    useEffect(() => {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,700;1,400&family=Inter:wght@400;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');

        try {
            const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
            const res = await fetch(`${API_BASE}/api/applications`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setStatus('success');
            } else {
                setStatus('error');
            }
        } catch (err) {
            console.error(err);
            setStatus('error');
        }
    };

    if (status === 'success') {
        return (
            <div className="h-screen w-full bg-[#f2efe9] flex items-center justify-center p-8 font-serif text-[#111] relative overflow-hidden">
                {/* Authentic Grain & Texture Layers */}
                <div className="fixed inset-0 pointer-events-none opacity-[0.12] z-50 mix-blend-multiply bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]"></div>
                <div className="fixed inset-0 pointer-events-none opacity-[0.08] z-40 bg-[url('https://www.transparenttextures.com/patterns/paper-fibers.png')]"></div>
                <div className="fixed inset-0 pointer-events-none z-50 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.15)_100%)]"></div>

                <div className="max-w-xl text-center space-y-12 relative z-30 mix-blend-multiply">
                    <h1 className="text-5xl md:text-7xl font-bold tracking-[-0.06em] leading-[0.85] italic blur-[0.4px] drop-shadow-sm">
                        Selection is <br />
                        now in progress.
                    </h1>
                    <div className="h-[2px] w-24 bg-[#111] mx-auto opacity-80 blur-[0.5px]"></div>
                    <p className="text-xl md:text-2xl leading-relaxed max-w-md mx-auto font-medium blur-[0.3px] opacity-90">
                        Thank you for your submission. Our curators are reviewing your artifacts and will respond should an opening align with your vision.
                    </p>
                    <div className="pt-12">
                        <a
                            href="/"
                            className="text-[11px] font-bold uppercase tracking-[0.3em] border-b-2 border-[#111] pb-1 hover:opacity-60 transition-opacity font-sans mix-blend-multiply"
                        >
                            Return to Paradise
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen w-full bg-[#f2efe9] relative overflow-y-auto overflow-x-hidden font-serif text-[#111] flex flex-col items-center">
            {/* Authentic Grain & Texture Layers - Fixed to Viewport */}
            <div className="fixed inset-0 pointer-events-none opacity-[0.15] z-50 mix-blend-multiply bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]"></div>
            <div className="fixed inset-0 pointer-events-none opacity-[0.08] z-40 bg-[url('https://www.transparenttextures.com/patterns/paper-fibers.png')]"></div>
            <div className="fixed inset-0 pointer-events-none z-50 bg-[radial-gradient(circle_at_center,transparent_50%,rgba(60,50,40,0.12)_100%)]"></div>

            <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start relative z-20 my-20 px-6 md:px-12 pb-32">

                {/* Headline Section */}
                <div className="lg:col-span-12 text-center mb-16 mix-blend-multiply">
                    <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-[-0.07em] leading-[0.8] mb-8 blur-[0.5px] text-[#0a0a0a]">
                        Residency Application <br /> Paradise Beach Club.
                    </h1>
                    <div className="max-w-2xl mx-auto flex flex-col items-center">
                        <div className="h-[2px] w-full bg-[#111] opacity-90 mb-8 blur-[0.3px]"></div>
                        <p className="text-xl md:text-2xl leading-snug italic opacity-90 mb-3 blur-[0.3px] font-medium">
                            Residency is limited. Submissions are reviewed by the Board. Admission is granted at discretion.
                        </p>
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] font-bold opacity-60 blur-[0.2px]">
                            Creator Residency Protocol · SECTION 80.C
                        </p>
                    </div>
                </div>

                {/* Form Section */}
                <div className="lg:col-start-3 lg:col-span-8">
                    <form onSubmit={handleSubmit} className="space-y-16 mix-blend-multiply">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                            <div className="space-y-4 group">
                                <label className="block font-sans text-[11px] font-bold uppercase tracking-[0.2em] opacity-50 group-focus-within:opacity-100 transition-opacity">Full Legal Name</label>
                                <input
                                    required
                                    type="text"
                                    placeholder="Enter name"
                                    className="w-full bg-transparent border-b-2 border-[#111] p-2 text-3xl tracking-[-0.03em] font-bold focus:outline-none placeholder-[#111]/10 blur-[0.3px] transition-all"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-4 group">
                                <label className="block font-sans text-[11px] font-bold uppercase tracking-[0.2em] opacity-50 group-focus-within:opacity-100 transition-opacity">Electronic Address</label>
                                <input
                                    required
                                    type="email"
                                    placeholder="email@server.com"
                                    className="w-full bg-transparent border-b-2 border-[#111] p-2 text-3xl tracking-[-0.03em] font-bold focus:outline-none placeholder-[#111]/10 blur-[0.3px] transition-all"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                            <div className="space-y-4 group">
                                <label className="block font-sans text-[11px] font-bold uppercase tracking-[0.2em] opacity-50 group-focus-within:opacity-100 transition-opacity">Public Identity</label>
                                <input
                                    type="text"
                                    placeholder="@handle"
                                    className="w-full bg-transparent border-b-2 border-[#111] p-2 text-3xl tracking-[-0.03em] font-bold focus:outline-none placeholder-[#111]/10 blur-[0.3px] transition-all"
                                    value={formData.instagram}
                                    onChange={e => setFormData({ ...formData, instagram: e.target.value })}
                                />
                            </div>
                            <div className="space-y-4 group">
                                <label className="block font-sans text-[11px] font-bold uppercase tracking-[0.2em] opacity-50 group-focus-within:opacity-100 transition-opacity">Reference Work</label>
                                <input
                                    type="url"
                                    placeholder="https://"
                                    className="w-full bg-transparent border-b-2 border-[#111] p-2 text-3xl tracking-[-0.03em] font-bold focus:outline-none placeholder-[#111]/10 blur-[0.3px] transition-all"
                                    value={formData.portfolioUrl}
                                    onChange={e => setFormData({ ...formData, portfolioUrl: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="space-y-4 group">
                            <label className="block font-sans text-[11px] font-bold uppercase tracking-[0.2em] opacity-50 group-focus-within:opacity-100 transition-opacity">Creative Manifesto</label>
                            <textarea
                                rows="3"
                                placeholder="Describe your vision in brief..."
                                className="w-full bg-[#111]/[0.03] border border-[#111]/20 rounded-none p-6 text-2xl tracking-[-0.02em] leading-relaxed focus:border-[#111] focus:outline-none transition-all placeholder-[#111]/20 resize-none italic blur-[0.2px]"
                                value={formData.note}
                                onChange={e => setFormData({ ...formData, note: e.target.value })}
                            ></textarea>
                        </div>
                    </form>

                    {/* Moved button OUTSIDE of form mix-blend-multiply container to ensure it pops */}
                    {/* But still controlled by form submission via onClick or native form submit if inside form tag. */}
                    {/* Re-nesting in form but removing blend mode from button container */}
                    <div className="pt-20 flex flex-col items-center space-y-10 relative z-30">
                        <button
                            onClick={handleSubmit}
                            disabled={status === 'submitting'}
                            className="w-full md:w-auto px-24 h-24 bg-[#111] text-[#f2efe9] font-sans font-bold uppercase tracking-[0.5em] text-[13px] hover:bg-[#000] active:scale-[0.99] transition-all disabled:opacity-50 flex items-center justify-center shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] blur-[0.3px] cursor-pointer"
                        >
                            {status === 'submitting' ? 'Processing...' : 'Submit for Review'}
                        </button>

                        <p className="max-w-sm text-center font-sans text-[9px] uppercase tracking-[0.25em] leading-loose opacity-40 blur-[0.2px] ml-4">
                            This application is a formal request for entry. Membership is not guaranteed.
                            Paradise reserves the right to refuse any operator / network.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Apply;
